'use client'
import Link from 'next/link'
import { usePathname, useSearchParams } from 'next/navigation'
import React, { FC, useState } from 'react'

import Globe from '@/components/svg/Globe'
import { localeNames } from '@/i18n'
import locales from '@/i18n/locales'
import { cn } from '@/lib/utils'

type Locale = keyof typeof localeNames

const parseLocale = (locale?: string): Locale => {
  if (locale?.slice(0, 2) === 'en') {
    return 'en'
  }
  if (locale?.slice(0, 2) === 'es') {
    return 'es'
  }
  return locale as Locale
}

export const findBestNavLocale = (loc: string, currentLoc: string) => {
  if (loc === 'es' || loc === 'en') {
    const country = currentLoc.slice(3)

    const locale = locales.find(
      (l: string) => l.slice(0, 2) === loc && l.slice(3) === country,
    )
    if (locale) return locale
    if (loc === 'es') return 'es-es'
    return 'en-us'
  }
  return loc
}

type Props = {
  reverse?: boolean
  locale: string
}

const LanguageDropdown: FC<Props> = ({ reverse, locale }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isOpen, setIsOpen] = useState(false)

  const onSelect = (newLocale: string) => {
    document.cookie = `NEXT_LOCALE=${newLocale}; max-age=31536000;`
    setIsOpen(false)
  }

  const pathnameWithoutLocale = pathname?.split('/').slice(2).join('/')

  const createLanguages = () =>
    Object.entries(localeNames)
      .filter(([loc]) => loc !== locale)
      .map(([newLocale, label], idx, options) => {
        const loc = findBestNavLocale(newLocale, locale ?? 'en-us')
        const href = searchParams?.toString()
          ? `/${loc}/${pathnameWithoutLocale}?${searchParams.toString()}`
          : `/${loc}/${pathnameWithoutLocale}`

        return (
          <li key={loc} className="w-full">
            <Link
              href={href}
              onClick={() => onSelect(loc)}
              prefetch={false}
              className={cn(
                'w-full flex items-start bg-black',
                'transition-all duration-300 hover:bg-primary-600',
                'opacity-0 group-[.menu-open]:opacity-100',
                'transition-all',
              )}
              style={{
                transitionDuration: `${
                  0.15 * (isOpen ? idx + 1 : options.length - idx)
                }s`,
              }}
            >
              <span className="w-full py-2 px-4 text-sm font-semibold text-white">
                {label}
              </span>
            </Link>
          </li>
        )
      })

  return (
    <div className="relative">
      {isOpen && (
        <div
          className="fixed inset-0 z-2"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(false)
          }}
        />
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-white lg:text-neutral-200 py-2 px-4 gap-4 cursor-pointer lg:hover:bg-primary-600 transition-all duration-300"
      >
        <Globe className="w-6 h-6" />
        <span className="w-full font-medium text-sm">
          {localeNames[parseLocale(locale)]}
        </span>
      </button>
      <ul
        className={cn(
          'flex flex-col items-start',
          'absolute right-0 min-w-full z-3',
          'transition-all duration-300',
          'lg:bg-black',
          {
            'max-h-0 overflow-hidden': !isOpen,
            'max-h-200 overflow-visible': isOpen,
            'top-full': !reverse,
            'bottom-full': reverse,
            'menu-open': isOpen,
          },
          'group',
        )}
      >
        {createLanguages()}
      </ul>
    </div>
  )
}

export default LanguageDropdown
